
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { extend } from "vee-validate";

import CsvUploader from "@/components/invite/CsvUploader.vue";
import UiButton from "@/components/button/ui-button.vue";
import UserInvitationData from "@/components/invite/UserInvitationData.vue";
import ModalTabs from "@/components/modal/ModalTabs.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import ManagerInvitationDisabledTooltip from "@/components/managers/ManagerInvitationDisabledTooltip.vue";
import InvitationPlaceholder from "@/components/managers/InvitationPlaceholder.vue";

import getAuthData from "@/utils/getAuthData";
import auth from "@/services/auth";
import { INVITATION, NOTIFICATION } from "@/entities/Events";
import Message, { MessageDuration } from '@/data/notificationsMessages';
import { Company } from "@/interface";
import { invitationModal } from "@/data/modalTabs";
import RouteParams from "@/entities/RouteParams";
import RouteNames from "@/entities/RouteNames";
import lambdaRequest from "@/helpers/lambdaRequest";

interface UserInterface {
  id: string | null;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  valid: boolean;
  processing: boolean;
}

@Component({
  components: {
    InvitationPlaceholder,
    ModalTabs,
    CsvUploader,
    UserInvitationData,
    UiButton,
    ModalFullScreen,
    PlusIcon,
    ModalSectionsDivider,
    ManagerInvitationDisabledTooltip,
  },
})
export default class ManagerInvite extends Vue {
  @Prop({ type: Object, default: undefined }) readonly company!: Company;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: String, required: true }) readonly companyBusinessName!: string;
  @Prop({ type: Boolean, default: false }) readonly managersUnlimited!: boolean;
  @Prop({ type: Number, required: true }) readonly activeManagersCount!: number;
  @Prop({ type: Number, required: true }) readonly accessAllowedManagersCount!: number;

  tabs: string[] = invitationModal;
  selectedTabIndex: number = 0;
  csvUploadCompleted: boolean = false;
  csvParseOutput: UserInterface[] = [];
  customMessage: string = "";
  userTemplate: UserInterface = {
    id: null,
    email: null,
    givenName: null,
    familyName: null,
    valid: false,
    processing: false,
  };
  users: UserInterface[] = [
    {
      id: null,
      email: "",
      givenName: "",
      familyName: "",
      valid: false,
      processing: false,
    },
  ];
  loading: boolean = false;

  get allowDelete(): boolean {
    return this.users.length > 1;
  }

  beforeMount() {
    extend("unique", {
      params: [{ name: "usersToCheck" }],
      validate: (v: string, params: Record<string, any>) => {
        let count = 0;
        if (params.usersToCheck.length > 1) {
          params.usersToCheck.forEach((u: UserInterface) => {
            if (u.email === v) {
              count++;
            }
          });
        }
        return count <= 1;
      },
      message: "Must be unique",
    });
    extend("managerExists", {
      params: [{ name: "usersToCheck" }],
      validate: async (v: string, params: Record<string, any>): Promise<boolean> => {
        const user = params.usersToCheck.find((u: UserInterface) => u.email === v);
        if (!user) {
          return false;
        }
        user.processing = true;
        try {
          const uri = `${process.env.VUE_APP_LAMBDA}/manager-invite-email-validation`;
          const { headers } = await getAuthData();
          const { data }: any = await this.axios.post(uri, {
            data: {
              email: v.toLowerCase(),
              companyId: this.companyId,
            }
          }, { headers });
          this.customMessage = data.message;
          return data.valid;
        } catch (error) {
          this.customMessage = error.message || error;
          return false
        } finally {
          user.processing = false;
        }
      },
      message: () => {
        return this.customMessage;
      },
    });
  }

  @Watch("selectedTabIndex")
  watchSelectedTabIndex(index: number): void {
    if (index === 0) {
      this.users = [{
        id: null,
        email: "",
        givenName: "",
        familyName: "",
        valid: false,
        processing: false,
      }];
      return;
    }
    this.csvParseOutput = [];
  }

  get allowedNumberOfCSVManagersReached(): boolean {
    if (this.managersUnlimited) {
      return false;
    }

    return this.csvParseOutput.length > this.availableNumberOfManagers;
  }

  get uploading(): boolean {
    return this.loading;
  }

  get invitationTabUsers(): any[] {
    return this.selectedTabIndex == 0 ? this.users : this.csvParseOutput;
  }

  get addingManagersAllowed(): boolean {
    if (this.managersUnlimited) {
      return true;
    }

    return this.availableNumberOfManagers > 0;
  }

  get managerSubscriptionLink(): RouteParams<{}> {
    return {
      name: RouteNames.PRICING,
    };
  }

  get availableNumberOfManagers(): number {
    return this.accessAllowedManagersCount - this.activeManagersCount
  }

  get allowedNumberOfManagersReached(): boolean {
    if (this.managersUnlimited) {
      return false;
    }

    const managersFieldsCount = this.users.length;
    return managersFieldsCount >= this.availableNumberOfManagers;
  }

  get submitButtonText(): string {
    return this.loading ? 'Loading...' : 'Invite';
  }
  updateTabIndex(index: number): void {
    this.selectedTabIndex = index;
  }

  handleDeleteRow(index: number): void {
    this.users.splice(index, 1);
    if (!this.users.length) {
      this.users = [{
        id: null,
        email: "",
        givenName: "",
        familyName: "",
        valid: false,
        processing: false,
      }]
    }
  }

  async invite(users: UserInterface[] | null = null): Promise<void> {
    try {
      this.loading = true;
      const addedUsers = users ? users : this.users;
      const allowedUsers = this.managersUnlimited ? addedUsers : addedUsers.slice(0, this.availableNumberOfManagers);
      const userObjects = allowedUsers.map((u) => ({
        givenName: u.givenName || "",
        familyName: u.familyName || "",
        email: (u.email || "").toLowerCase(),
      }));
      await this._inviteManagers(userObjects, this.companyId, this.companyBusinessName);
      if (users) {
        this.csvUploadCompleted = true;
      }
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Invited"),
      );

      this.clearUsers();
      this.clearCsv()
      this.$eventBus.emit(INVITATION);
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(e.message),
      );
    } finally {
      this.loading = false;
    }
  }
  clearUsers(): void {
    this.users = [{
      id: null,
      email: "",
      givenName: "",
      familyName: "",
      valid: false,
      processing: false,
    }];
  }
  clearCsv(): void {
    this.csvParseOutput = [];
  }
  add(): void {
    this.users.push({ ...this.userTemplate });
  }

  handleCancel(): void {
    this.$emit('input', false);
    this.clearUsers();
    this.clearCsv();
  }

  async _inviteManagers(
    users: any[],
    company_id: String,
    company_business_name: String,
  ): Promise<void> {
    const { headers } = await getAuthData();
    const uri = `${process.env.VUE_APP_LAMBDA}/create-invite-managers`;
    const inviteUserResp = await this.axios.post(
      uri,
      {
        users,
        company_id,
        company_business_name
      },
      { headers }
    );
    // if(inviteUserResp){
    //   this._AdminUpdateUserAttributes(inviteUserResp.data.managerIds[0].user_id);
    // }
    return;
  }
  async _AdminUpdateUserAttributes(
    user_id: string
  ): Promise<void> {
    const { headers } = await getAuthData();
    const uri = `${process.env.VUE_APP_LAMBDA}/verify-User`;
    const inviteUserResp = await this.axios.post(
      uri,
      {
        user_id
      },
      { headers }
    );
  }
  extensionAlert(): void {
    this.$eventBus.emit(
      NOTIFICATION,
      Message.errorMessage("Make sure your file has .csv extension"),
    );
  }
  invalidHeaderRow(): void {
    this.$eventBus.emit(
      NOTIFICATION,
      Message.errorMessage(
        "Make sure your csv file has a header row with values 'email', 'given_name', 'family_name'",
        MessageDuration.MEDIUM
      ),
    );
  }
}
